/**
 * Frontend Stock Upload Helper Functions
 *
 * Pure functions for stock data transformation and validation
 * Mirrors backend logic in server/src/utils/bulkUploadHelpers.ts
 */

/**
 * Validates that a quantity is a positive integer
 * Returns error message if invalid, null if valid
 *
 * @param {any} value - The value to validate
 * @param {string} fieldName - Optional field name for error message
 * @returns {string|null} Error message or null if valid
 */
export const validatePositiveInteger = (value, fieldName = "Quantity") => {
  const num = Number(value);
  if (!Number.isInteger(num) || num < 1) {
    return `${fieldName} must be a positive whole number`;
  }
  return null;
};

/**
 * Validates that a batch number is not empty, whitespace-only, and is alphanumeric
 * Allows letters, numbers, and hyphens
 * Returns error message if invalid, null if valid
 *
 * @param {any} value - The batch number to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateBatchNumber = (value) => {
  if (!value || (typeof value === "string" && !value.trim())) {
    return "Batch number is required";
  }

  const batchStr = String(value).trim();
  // Allow letters, numbers, and hyphens
  const alphanumericPattern = /^[a-zA-Z0-9-]+$/;
  if (!alphanumericPattern.test(batchStr)) {
    return "Batch number must be alphanumeric (letters, numbers, and hyphens only)";
  }

  return null;
};

/**
 * Validates that an expiry date is not in the past
 * Returns error message if invalid, null if valid
 *
 * @param {Date|string} dateValue - The date to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateExpiryDateNotPast = (dateValue) => {
  const expiryDate = dateValue instanceof Date ? dateValue : new Date(dateValue);

  if (isNaN(expiryDate.getTime())) {
    return "Invalid expiry date format";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (expiryDate < today) {
    return "Expiry date cannot be in the past";
  }

  return null;
};

/**
 * Validates all stock item fields and returns array of error messages
 *
 * @param {Object} item - Stock item with normalized field names
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateStockItem = (item) => {
  const errors = [];

  // Validate quantity is positive integer
  if (item.currentStock !== undefined && item.currentStock !== null && item.currentStock !== "") {
    const quantityError = validatePositiveInteger(item.currentStock, "Current stock");
    if (quantityError) {
      errors.push(quantityError);
    }
  }

  // Validate batch number
  const batchError = validateBatchNumber(item.batchNumber);
  if (batchError) {
    errors.push(batchError);
  }

  // Validate expiry date not in past
  if (item.expiryDate) {
    const expiryError = validateExpiryDateNotPast(item.expiryDate);
    if (expiryError) {
      errors.push(expiryError);
    }
  }

  return errors;
};

/**
 * Transform stock upload errors into Excel-ready row format
 * Extracts stock item data and error reason into a flat structure
 * suitable for Excel export
 *
 * @param {Array} errors - Array of error objects from bulk upload response
 * @returns {Array} Array of objects with stock fields + reason column
 */
export const transformStockErrorsToExcelRows = (errors) => {
  return errors.map((error) => {
    const stockItem = error.data || {};
    return {
      "Row": error.row,
      "Medicine ID": stockItem.medicineId || "",
      "Batch Number": stockItem.batchNumber || "",
      "Expiry Date": stockItem.expiryDate || "",
      "Current Stock": stockItem.currentStock || "",
      "Unit Cost": stockItem.unitCost || "",
      "Supplier": stockItem.supplier || "",
      "Storage Location": stockItem.storageLocation || "",
      "Notes": stockItem.notes || "",
      "Reason": error.error || "Unknown error",
    };
  });
};

/**
 * Get Excel column width configuration for stock error report
 * Returns array of column width objects for XLSX library
 *
 * @returns {Array} Array of column width objects
 */
export const getStockErrorReportColumnWidths = () => {
  return [
    { wch: 6 },   // Row
    { wch: 20 },  // Medicine ID
    { wch: 15 },  // Batch Number
    { wch: 12 },  // Expiry Date
    { wch: 12 },  // Current Stock
    { wch: 12 },  // Unit Cost
    { wch: 20 },  // Supplier
    { wch: 15 },  // Storage Location
    { wch: 25 },  // Notes
    { wch: 50 },  // Reason
  ];
};
